import { Injectable, Inject } from '@angular/core';
import { Observable } from 'rxjs';
import {
  Order,
  NewPaymentResponse,
  ProcessPayPalResponse,
  OrderPromotionType,
  OrderPromotionByCount,
  OrderPromotionByAmount,
  CartPrice
} from './order-models';
import { AutoTablePageableResponse } from '@muchencute/mrrs-types';
import { HttpClient } from '@angular/common/http';
import { CORE_CONFIG } from '../environment';

@Injectable({
  providedIn: 'root'
})
export class OrderService {

  private _baseURL: string;

  private _sendOrderURL: string;

  private _newPaymentURL: string;

  private _processPayPalURL: string;

  private _cancelPayPalURL: string;

  private _promotionURL: string;

  private _orderPromotionURL: string;

  constructor(private httpClient: HttpClient,
    @Inject(CORE_CONFIG) private coreConfig: any) {
    this._baseURL = `${this.coreConfig.endpoint}api/v1/order`;
    this._sendOrderURL = `${this.coreConfig.endpoint}api/v1/sendorder`;
    this._newPaymentURL = `${this.coreConfig.endpoint}api/v1/pay`;
    this._processPayPalURL = `${this.coreConfig.endpoint}api/v1/processpaypal`;
    this._cancelPayPalURL = `${this.coreConfig.endpoint}api/v1/cancelpaypal`;
    this._promotionURL = `${this.coreConfig.endpoint}api/v1/promotion`;
    this._orderPromotionURL = `${this.coreConfig.endpoint}api/v1/orderpromotion`;
  }

  getOrderList(params: { [param: string]: any }): Observable<AutoTablePageableResponse<Order>> {
    return this.httpClient.get<AutoTablePageableResponse<Order>>(`${this._baseURL}s`, {
      params: params
    });
  }

  newOrder(products: { code: number; priceID: number }[]): Observable<Order> {
    return this.httpClient.post<Order>(`${this._baseURL}`, { products });
  }

  newOrderAnonymous(email: string, products: { code: number; priceID: number }[]): Observable<Order> {
    return this.httpClient.post<Order>(`${this._baseURL}anonymous`, { email, products });
  }

  getOrder(orderCode: string): Observable<Order> {
    return this.httpClient.get<Order>(`${this._baseURL}/${orderCode}`);
  }

  getOrderAnonymous(orderCode: string): Observable<Order> {
    return this.httpClient.get<Order>(`${this._baseURL}anonymous/${orderCode}`);
  }

  updateOrder(orderCode: string, params: { [name: string]: string }): Observable<any> {
    return this.httpClient.put(`${this._baseURL}/${orderCode}`, {
      orderCode, ...params
    });
  }

  sendOrder(code: string, email: string): Observable<any> {
    return this.httpClient.post(`${this._sendOrderURL}`, {}, {
      params: {
        code, email
      }
    });
  }

  pay(orderCode: string, paymentMethod: string, returnURL = '', cancelURL = ''): Observable<NewPaymentResponse> {
    return this.httpClient.post<NewPaymentResponse>(`${this._newPaymentURL}`,
      {
        orderCode,
        paymentMethod,
        returnURL,
        cancelURL
      }
    );
  }

  processPayPal(orderCode: string, paymentID: string, payerID: string): Observable<ProcessPayPalResponse> {
    return this.httpClient.get<ProcessPayPalResponse>(`${this._processPayPalURL}`, {
      params: { orderCode, paymentID, payerID }
    });
  }

  cancelPayPal(orderCode: string): Observable<any> {
    return this.httpClient.get(`${this._cancelPayPalURL}`, {
      params: { orderCode }
    });
  }

  discountDate(manufacturerName: string, discountRate: number, discountStartDate: string, discountDeadline: string): Observable<any> {
    return this.httpClient.post(`${this._promotionURL}/discountdate`, {
      manufacturerName,
      discountRate,
      discountStartDate,
      discountDeadline
    });
  }

  discountCreationDate(manufacturerName: string, startCreationDate: string, endCreationDate: string,
    discountRate: number, discountStartDate: string, discountDeadline: string): Observable<any> {
    return this.httpClient.post(`${this._promotionURL}/discountcreationdate`, {
      manufacturerName,
      startCreationDate,
      endCreationDate,
      discountRate,
      discountStartDate,
      discountDeadline
    });
  }

  discountCatalog(manufacturerName: string, catalog: number, discountRate: number,
    discountStartDate: string, discountDeadline: string): Observable<any> {
    return this.httpClient.post(`${this._promotionURL}/discountcatalog`, {
      manufacturerName,
      catalog,
      discountRate,
      discountStartDate,
      discountDeadline
    });
  }

  discountIndividual(code: number, discountRate: number, discountStartDate: string, discountDeadline: string): Observable<any> {
    return this.httpClient.post(`${this._promotionURL}/discountindividual`, {
      code,
      discountRate,
      discountStartDate,
      discountDeadline
    });
  }

  newOrderPromotionByCount(
    manufacturerName: string,
    description: string,
    startDate: string,
    endDate: string,
    count: number,
    discountRate: number
  ): Observable<any> {
    return this.httpClient.post(`${this._orderPromotionURL}`, {
      type: OrderPromotionType.ByCount,
      description,
      manufacturerName,
      startDate,
      endDate,
      count,
      discountRate
    });
  }

  newOrderPromotionByAmount(manufacturerName: string,
    description: string,
    startDate: string,
    endDate: string,
    amount: number,
    discountAmount: number
  ): Observable<any> {
    return this.httpClient.post(`${this._orderPromotionURL}`, {
      type: OrderPromotionType.ByAmount,
      description,
      manufacturerName,
      startDate,
      endDate,
      amount,
      discountAmount
    });
  }

  getOrderPromotionsByCount(params: { [name: string]: any }): Observable<AutoTablePageableResponse<OrderPromotionByCount>> {
    return this.httpClient.get<AutoTablePageableResponse<OrderPromotionByCount>>(`${this._orderPromotionURL}s`, {
      params: { type: OrderPromotionType.ByCount, ...params }
    });
  }

  getOrderPromotionsByAmount(params: { [name: string]: any }): Observable<AutoTablePageableResponse<OrderPromotionByAmount>> {
    return this.httpClient.get<AutoTablePageableResponse<OrderPromotionByAmount>>(`${this._orderPromotionURL}s`, {
      params: { type: OrderPromotionType.ByAmount, ...params }
    });
  }

  removeOrderPromotion(type: OrderPromotionType, manufacturerName: string): Observable<any> {
    return this.httpClient.delete(`${this._orderPromotionURL}`, {
      params: {
        type, manufacturerName
      }
    });
  }

  updateOrderPromotionByCount(manufacturerName: string,
    description: string,
    startDate: string,
    endDate: string,
    count: number,
    discountRate: number
  ): Observable<any> {
    return this.httpClient.put(`${this._orderPromotionURL}`, {
      type: OrderPromotionType.ByCount,
      description,
      manufacturerName,
      startDate,
      endDate,
      count,
      discountRate
    });
  }

  updateOrderPromotionByAmount(manufacturerName: string,
    description: string,
    startDate: string,
    endDate: string,
    amount: number,
    discountAmount: number): Observable<any> {
    return this.httpClient.put(`${this._orderPromotionURL}`, {
      type: OrderPromotionType.ByAmount,
      description,
      manufacturerName,
      startDate,
      endDate,
      amount,
      discountAmount
    });
  }

  getOrderPromotionByCount(manufacturerName: string): Observable<OrderPromotionByCount> {
    return this.httpClient.get<OrderPromotionByCount>(`${this._orderPromotionURL}`, {
      params: {
        type: OrderPromotionType.ByCount,
        manufacturerName
      }
    });
  }

  getOrderPromotionByAmount(manufacturerName: string): Observable<OrderPromotionByAmount> {
    return this.httpClient.get<OrderPromotionByAmount>(`${this._orderPromotionURL}`, {
      params: {
        type: OrderPromotionType.ByAmount,
        manufacturerName
      }
    });
  }

  getCartPrice(codes: string[], priceIDs: string[]): Observable<CartPrice> {
    return this.httpClient.get<CartPrice>(`${this._baseURL}cartprice`, {
      params: {
        code: codes,
        priceID: priceIDs
      }
    });
  }

}
